import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import './SMSPlansPage.css';

const CloseIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

function SMSPlansPage() {
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [selectedPlan, setSelectedPlan] = useState(200);
  const [dailySpendLimit, setDailySpendLimit] = useState(20);

  const smsPlans = [
    { credits: 200, monthlyPrice: 9, yearlyPrice: 9, savePercent: 0, additionalCredits: 0.055 },
    { credits: 500, monthlyPrice: 22, yearlyPrice: 20, savePercent: 11, additionalCredits: 0.05 },
    { credits: 1000, monthlyPrice: 45, yearlyPrice: 35, savePercent: 22, additionalCredits: 0.045 },
    { credits: 2500, monthlyPrice: 112, yearlyPrice: 75, savePercent: 33, additionalCredits: 0.04 },
    { credits: 5000, monthlyPrice: 223, yearlyPrice: 125, savePercent: 44, additionalCredits: 0.035 },
    { credits: 10000, monthlyPrice: 450, yearlyPrice: 200, savePercent: 56, additionalCredits: 0.03 },
  ];

  const formatCredits = (num) => {
    return num.toLocaleString();
  };

  const getCurrentPrice = () => {
    const plan = smsPlans.find(p => p.credits === selectedPlan);
    return billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice;
  };

  return (
    <div className="sms-plans-page">
      {/* Header */}
      <header className="sms-header">
        <Link to="/" className="close-button">
          <CloseIcon />
        </Link>
        <h1 className="header-title">Change plan</h1>
      </header>

      {/* Main Content */}
      <main className="sms-main">
        <div className="sms-content">
          {/* Left Section - Plans Table */}
          <div className="sms-plans-section">
            <h2 className="sms-title">SMS Plans</h2>
            
            <div className="plans-table-container">
              <table className="plans-table">
                <thead>
                  <tr>
                    {smsPlans.map((plan) => (
                      <th key={plan.credits}>
                        <div className="plan-credits">{formatCredits(plan.credits)}</div>
                        <div className="plan-credits-label">credits/month</div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {smsPlans.map((plan) => (
                      <td key={plan.credits}>
                        <div className="plan-price">
                          ${billingCycle === 'yearly' ? plan.yearlyPrice : plan.monthlyPrice}/month
                        </div>
                        {billingCycle === 'yearly' && plan.savePercent > 0 && (
                          <div className="plan-save">Save {plan.savePercent}%</div>
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr>
                    {smsPlans.map((plan) => (
                      <td key={plan.credits}>
                        {selectedPlan === plan.credits ? (
                          <div className="plan-selected">
                            <span className="selected-toggle active"></span>
                            <span className="selected-text">Selected</span>
                          </div>
                        ) : (
                          <button 
                            className="select-plan-btn"
                            onClick={() => setSelectedPlan(plan.credits)}
                          >
                            <span>Select</span>
                            <span className="select-toggle"></span>
                          </button>
                        )}
                      </td>
                    ))}
                  </tr>
                  <tr className="additional-credits-row">
                    {smsPlans.map((plan) => (
                      <td key={plan.credits}>
                        <div className="additional-credits-label">Additional credits:</div>
                        <div className="additional-credits-price">${plan.additionalCredits.toFixed(plan.additionalCredits < 0.1 ? 3 : 2)}</div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>

            <p className="enterprise-note">
              Send more than 10,000 messages a month? <a href="#" className="contact-link">Contact us</a> for enterprise pricing or to discuss which plan is best for you.
            </p>

            <div className="sms-notes">
              <p><strong>US & Canada:</strong> 1 SMS message = 1 credit (160 characters). 1 MMS (text message with image) = 2 credits.</p>
              <p>* Other country pricing varies widely. Contact support for pricing in specific countries.</p>
              <p>** Prices may change from time to time without notice. Additional carrier fees may be charged in addition to the prices above. Please see the carrier fees section <a href="#" className="contact-link">on this page</a> for details.</p>
            </div>
          </div>

          {/* Right Section - Sidebar */}
          <aside className="sms-sidebar">
            <div className="sidebar-card">
              <div className="billing-toggle-row">
                <span className="billing-label">Billed yearly</span>
                <button 
                  className={`billing-toggle-btn ${billingCycle === 'yearly' ? 'active' : ''}`}
                  onClick={() => setBillingCycle(billingCycle === 'yearly' ? 'monthly' : 'yearly')}
                >
                  <span className={`toggle-track ${billingCycle === 'yearly' ? 'active' : ''}`}></span>
                </button>
              </div>

              <div className="price-display">
                <span className="price-currency">$</span>
                <span className="price-amount">{getCurrentPrice()}</span>
                <span className="price-period">per month</span>
              </div>

              <button className="change-plan-btn">Change plan</button>

              <p className="tax-note">Subject to local sales tax rates based on your billing address</p>

              <a href="#" className="cancel-link">Cancel my SMS Plan</a>
            </div>

            <div className="sidebar-card limits-card">
              <h3 className="limits-title">SMS Spent Limits</h3>
              <p className="limits-description">
                Sometimes good people do bad things with automations and send a whole lot of messages on accident, resulting in big bills. This setting limits the amount you can spend in a day on SMS messages. Once reached, we'll stop sending messages until the next day, or you change this setting. SMS messages in automations, rules or sequences will not send nor be queued to send later.
              </p>
              <div className="limits-row">
                <span className="limits-label">Daily spend limit: ${dailySpendLimit}</span>
                <button className="edit-btn">EDIT</button>
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

export default SMSPlansPage;
